import { logger } from './logger';

export interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  publishedAt: string;
  thumbnails: {
    default?: { url: string; width: number; height: number };
    medium?: { url: string; width: number; height: number };
    high?: { url: string; width: number; height: number };
    standard?: { url: string; width: number; height: number };
    maxres?: { url: string; width: number; height: number };
  };
  duration: string; // ISO 8601 duration format (PT4M13S)
  channelId: string;
  channelTitle: string;
}

export interface YouTubeChannel {
  id: string;
  title: string;
  description: string;
  thumbnails: {
    default?: { url: string; width: number; height: number };
    medium?: { url: string; width: number; height: number };
    high?: { url: string; width: number; height: number };
  };
}

const YOUTUBE_API_BASE = 'https://www.googleapis.com/youtube/v3';

export class YouTubeAPI {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  private async request(endpoint: string, params: Record<string, string> = {}) {
    // Use server proxy instead of direct YouTube API calls
    const response = await fetch('/api/youtube/proxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        endpoint,
        params,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`YouTube API error: ${response.status} ${response.statusText} - ${errorData.error?.message || 'Unknown error'}`);
    }

    return response.json();
  }

  async getChannelByUsername(username: string): Promise<YouTubeChannel | null> {
    try {
      const data = await this.request('/channels', {
        part: 'id,snippet',
        forUsername: username,
      });

      if (!data.items || data.items.length === 0) {
        return null;
      }

      const channel = data.items[0];
      return {
        id: channel.id,
        title: channel.snippet.title,
        description: channel.snippet.description,
        thumbnails: channel.snippet.thumbnails,
      };
    } catch (error) {
      logger.error('Error fetching channel by username:', error);
      return null;
    }
  }

  async getChannelByHandle(handle: string): Promise<YouTubeChannel | null> {
    try {
      // Remove @ symbol if present
      const cleanHandle = handle.startsWith('@') ? handle.slice(1) : handle;
      
      const data = await this.request('/channels', {
        part: 'id,snippet',
        forHandle: cleanHandle,
      });

      if (!data.items || data.items.length === 0) {
        return null;
      }

      const channel = data.items[0];
      return {
        id: channel.id,
        title: channel.snippet.title,
        description: channel.snippet.description,
        thumbnails: channel.snippet.thumbnails,
      };
    } catch (error) {
      logger.error('Error fetching channel by handle:', error);
      return null;
    }
  }

  async getChannelById(channelId: string): Promise<YouTubeChannel | null> {
    try {
      const data = await this.request('/channels', {
        part: 'id,snippet',
        id: channelId,
      });

      if (!data.items || data.items.length === 0) {
        return null;
      }

      const channel = data.items[0];
      return {
        id: channel.id,
        title: channel.snippet.title,
        description: channel.snippet.description,
        thumbnails: channel.snippet.thumbnails,
      };
    } catch (error) {
      logger.error('Error fetching channel by ID:', error);
      return null;
    }
  }

  async getPlaylistVideos(playlistId: string, maxResults: number = 50): Promise<YouTubeVideo[]> {
    try {
      logger.log(`Fetching videos from playlist: ${playlistId}`);
      
      // Get videos from the playlist
      const playlistData = await this.request('/playlistItems', {
        part: 'snippet',
        playlistId: playlistId,
        maxResults: maxResults.toString(),
      });

      if (!playlistData.items || playlistData.items.length === 0) {
        logger.log('No videos found in playlist');
        return [];
      }

      // Get video IDs
      const videoIds = playlistData.items.map((item: any) => item.snippet.resourceId.videoId);
      logger.log(`Found ${videoIds.length} videos in playlist`);

      // Get detailed video information including duration
      const videosData = await this.request('/videos', {
        part: 'snippet,contentDetails',
        id: videoIds.join(','),
      });

      return videosData.items.map((video: any) => ({
        id: video.id,
        title: video.snippet.title,
        description: video.snippet.description,
        publishedAt: video.snippet.publishedAt,
        thumbnails: video.snippet.thumbnails,
        duration: video.contentDetails.duration,
        channelId: video.snippet.channelId,
        channelTitle: video.snippet.channelTitle,
      }));
    } catch (error) {
      logger.error('Error fetching playlist videos:', error);
      throw error;
    }
  }

  async getChannelVideos(channelId: string, maxResults: number = 50): Promise<YouTubeVideo[]> {
    try {
      // First get the uploads playlist ID
      const channelData = await this.request('/channels', {
        part: 'contentDetails',
        id: channelId,
      });

      if (!channelData.items || channelData.items.length === 0) {
        throw new Error('Channel not found');
      }

      const uploadsPlaylistId = channelData.items[0].contentDetails.relatedPlaylists.uploads;

      // Get videos from the uploads playlist
      const playlistData = await this.request('/playlistItems', {
        part: 'snippet',
        playlistId: uploadsPlaylistId,
        maxResults: maxResults.toString(),
      });

      if (!playlistData.items || playlistData.items.length === 0) {
        // Try multiple fallback methods for different video types
        logger.log('No videos found in uploads playlist, trying search methods...');
        
        const allVideos: any[] = [];
        
        // Method 1: Regular search for videos
        try {
          const searchData = await this.request('/search', {
            part: 'snippet',
            channelId: channelId,
            type: 'video',
            maxResults: Math.min(maxResults, 25).toString(),
            order: 'date'
          });

          if (searchData.items && searchData.items.length > 0) {
            allVideos.push(...searchData.items.map((item: any) => item.id.videoId));
          }
        } catch (error) {
          logger.warn('Regular search failed:', error);
        }

        // Method 2: Search specifically for Shorts (duration < 60 seconds)
        try {
          const shortsSearch = await this.request('/search', {
            part: 'snippet',
            channelId: channelId,
            type: 'video',
            maxResults: Math.min(maxResults, 25).toString(),
            order: 'date',
            videoDuration: 'short' // This targets videos under 4 minutes, we'll filter further
          });

          if (shortsSearch.items && shortsSearch.items.length > 0) {
            const shortsVideoIds = shortsSearch.items.map((item: any) => item.id.videoId);
            // Add shorts that aren't already included
            for (const id of shortsVideoIds) {
              if (!allVideos.includes(id)) {
                allVideos.push(id);
              }
            }
          }
        } catch (error) {
          logger.warn('Shorts search failed:', error);
        }

        // Method 3: Search by channel handle/name (can find unlisted content)
        try {
          const handleSearch = await this.request('/search', {
            part: 'snippet',
            q: '@wellbound',
            type: 'video',
            maxResults: Math.min(maxResults, 25).toString(),
            order: 'date'
          });

          if (handleSearch.items && handleSearch.items.length > 0) {
            const handleVideoIds = handleSearch.items
              .filter((item: any) => item.snippet.channelId === channelId)
              .map((item: any) => item.id.videoId);
            
            for (const id of handleVideoIds) {
              if (!allVideos.includes(id)) {
                allVideos.push(id);
              }
            }
          }
        } catch (error) {
          logger.warn('Handle search failed:', error);
        }

        // Method 4: Search by channel title
        try {
          const titleSearch = await this.request('/search', {
            part: 'snippet',
            q: 'Wellbound+',
            type: 'video',
            maxResults: Math.min(maxResults, 25).toString(),
            order: 'date'
          });

          if (titleSearch.items && titleSearch.items.length > 0) {
            const titleVideoIds = titleSearch.items
              .filter((item: any) => item.snippet.channelId === channelId)
              .map((item: any) => item.id.videoId);
            
            for (const id of titleVideoIds) {
              if (!allVideos.includes(id)) {
                allVideos.push(id);
              }
            }
          }
        } catch (error) {
          logger.warn('Title search failed:', error);
        }

        // Method 5: Try activities endpoint (can sometimes show unlisted content)
        try {
          const activitiesSearch = await this.request('/activities', {
            part: 'snippet,contentDetails',
            channelId: channelId,
            maxResults: Math.min(maxResults, 25).toString()
          });

          if (activitiesSearch.items && activitiesSearch.items.length > 0) {
            const activityVideoIds = activitiesSearch.items
              .filter((item: any) => item.snippet.type === 'upload' && item.contentDetails?.upload?.videoId)
              .map((item: any) => item.contentDetails.upload.videoId);
            
            for (const id of activityVideoIds) {
              if (!allVideos.includes(id)) {
                allVideos.push(id);
              }
            }
          }
        } catch (error) {
          logger.warn('Activities search failed:', error);
        }

        // Method 6: For unlisted content, try searching for specific keywords that might be in your video titles/descriptions
        try {
          const keywordSearches = ['wellbound shorts', 'wellbound+ short', '@wellbound shorts'];
          
          for (const keyword of keywordSearches) {
            const keywordSearch = await this.request('/search', {
              part: 'snippet',
              q: keyword,
              type: 'video',
              maxResults: '20',
              order: 'date'
            });

            if (keywordSearch.items && keywordSearch.items.length > 0) {
              const keywordVideoIds = keywordSearch.items
                .filter((item: any) => item.snippet.channelId === channelId)
                .map((item: any) => item.id.videoId);
              
              for (const id of keywordVideoIds) {
                if (!allVideos.includes(id)) {
                  allVideos.push(id);
                  logger.log(`Found video via keyword "${keyword}": ${id}`);
                }
              }
            }
          }
        } catch (error) {
          logger.warn('Keyword search failed:', error);
        }

        if (allVideos.length === 0) {
          logger.log('No videos found via any search method. This is likely due to:');
          logger.log('1. Videos are private (not accessible via API)');
          logger.log('2. Videos are unlisted with no discoverable keywords');
          logger.log('3. Videos require special permissions/authentication');
          logger.log('4. Channel has no public content');
          return [];
        }

        // Remove duplicates and limit results
        const uniqueVideoIds = Array.from(new Set(allVideos)).slice(0, maxResults);

        // Get detailed video information including duration
        const videosData = await this.request('/videos', {
          part: 'snippet,contentDetails',
          id: uniqueVideoIds.join(','),
        });

        return videosData.items.map((video: any) => ({
          id: video.id,
          title: video.snippet.title,
          description: video.snippet.description,
          publishedAt: video.snippet.publishedAt,
          thumbnails: video.snippet.thumbnails,
          duration: video.contentDetails.duration,
          channelId: video.snippet.channelId,
          channelTitle: video.snippet.channelTitle,
        }));
      }

      // Get video IDs
      const videoIds = playlistData.items.map((item: any) => item.snippet.resourceId.videoId);

      // Get detailed video information including duration
      const videosData = await this.request('/videos', {
        part: 'snippet,contentDetails',
        id: videoIds.join(','),
      });

      return videosData.items.map((video: any) => ({
        id: video.id,
        title: video.snippet.title,
        description: video.snippet.description,
        publishedAt: video.snippet.publishedAt,
        thumbnails: video.snippet.thumbnails,
        duration: video.contentDetails.duration,
        channelId: video.snippet.channelId,
        channelTitle: video.snippet.channelTitle,
      }));
    } catch (error) {
      logger.error('Error fetching channel videos:', error);
      throw error;
    }
  }

  static getBestThumbnail(video: YouTubeVideo): string | undefined {
    const { thumbnails } = video;
    
    // Priority order: maxres > standard > high > medium > default
    if (thumbnails.maxres) return thumbnails.maxres.url;
    if (thumbnails.standard) return thumbnails.standard.url;
    if (thumbnails.high) return thumbnails.high.url;
    if (thumbnails.medium) return thumbnails.medium.url;
    if (thumbnails.default) return thumbnails.default.url;
    
    return undefined;
  }

  async debugChannelAccess(channelId: string): Promise<void> {
    logger.log(`=== DEBUG: Analyzing channel ${channelId} ===`);
    
    try {
      // Test 1: Channel info
      const channelInfo = await this.request('/channels', {
        part: 'snippet,contentDetails,statistics',
        id: channelId,
      });
      
      if (channelInfo.items && channelInfo.items.length > 0) {
        const channel = channelInfo.items[0];
        logger.log(`Channel: ${channel.snippet.title}`);
        logger.log(`Uploads playlist: ${channel.contentDetails.relatedPlaylists.uploads}`);
        logger.log(`Video count: ${channel.statistics?.videoCount || 'N/A'}`);
      }

      // Test 2: Try uploads playlist
      if (channelInfo.items?.[0]?.contentDetails?.relatedPlaylists?.uploads) {
        try {
          const playlistItems = await this.request('/playlistItems', {
            part: 'snippet',
            playlistId: channelInfo.items[0].contentDetails.relatedPlaylists.uploads,
            maxResults: '5',
          });
          logger.log(`Uploads playlist has ${playlistItems.items?.length || 0} accessible items`);
        } catch (e) {
          logger.log('Uploads playlist not accessible:', e);
        }
      }

      // Test 3: Direct search methods including unlisted detection
      const searchMethods: Array<{ name: string; params: Record<string, string> }> = [
        { name: 'Regular search', params: { part: 'snippet', channelId, type: 'video', maxResults: '5' } },
        { name: 'Short videos', params: { part: 'snippet', channelId, type: 'video', maxResults: '5', videoDuration: 'short' } },
        { name: 'Recent videos', params: { part: 'snippet', channelId, type: 'video', maxResults: '5', order: 'date' } },
        { name: 'Handle search', params: { part: 'snippet', q: '@wellbound', type: 'video', maxResults: '5' } },
        { name: 'Title search', params: { part: 'snippet', q: 'Wellbound+', type: 'video', maxResults: '5' } },
      ];

      for (const method of searchMethods) {
        try {
          const result = await this.request('/search', method.params);
          logger.log(`${method.name}: ${result.items?.length || 0} results`);
        } catch (e) {
          logger.log(`${method.name}: Failed -`, e);
        }
      }
    } catch (error) {
      logger.error('Debug failed:', error);
    }
    
    logger.log('=== DEBUG COMPLETE ===');
  }

  static parseDuration(isoDuration: string): number {
    // Convert ISO 8601 duration (PT4M13S) to seconds
    const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;

    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);

    return hours * 3600 + minutes * 60 + seconds;
  }

  static getEmbedUrl(videoId: string): string {
    return `https://www.youtube.com/embed/${videoId}`;
  }

  static getWatchUrl(videoId: string): string {
    return `https://www.youtube.com/watch?v=${videoId}`;
  }
}